import { z } from "zod";
import { useParams, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { Input } from "@/components/ui/input/input";

import { Separator } from "@/components/ui/separator/separator";
import { UploadAttachment } from "./upload-attachement";
import {
  createNewLoanRepaymentByAdmin,
  takeActionOnLoanRepaymentRequestAsAdmin,
} from "@/config/mutation/admin";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar/calendar";

const formSchema = z.object({
  amount: z.coerce.number().min(0).default(0),
  attachment: z.string().optional(),
  date: z.date(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateRepaymentRequest = () => {
  const [searchParams] = useSearchParams();
  const empId = searchParams.get("employee");

  const adminKey = Cookies.get("key") as string;
  const { loanId } = useParams() as {
    loanId: string;
  };
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: 0,
      attachment: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewLoanRepaymentByAdmin(adminKey),
  });

  const { mutateAsync } = useMutation({
    mutationFn: takeActionOnLoanRepaymentRequestAsAdmin(adminKey),
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    mutate(
      {
        ...values,
        loanId,
        employeeId: empId as string,
      },
      {
        onSuccess: async (data) => {
          //data?.paymentRequest?._id
          // console.log("hanji", data);

          await mutateAsync({
            repaymentRequestId: data?.paymentRequest?._id,
            status: "Approved",
          });
          await queryClient.invalidateQueries({
            queryKey: ["other-loan", loanId],
          });

          await queryClient.invalidateQueries({
            queryKey: ["other-loans"],
          });

          await queryClient.invalidateQueries({
            queryKey: ["admin-repayment-requests", loanId],
          });

          toast.success("Loan Repayment Created!");

          setTimeout(() => {
            form.setValue("amount", 0);
            form.setValue("attachment", "");
          }, 1000);

          setOpen(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING LOAN REPAYMENT", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <Button size="sm">Create Repayment</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="tracking-normal">
              Create Repayment
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                  <FormField
                    name="amount"
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Amount</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              disabled={isPending}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem className="flex flex-col mt-4">
                        <FormLabel>Date of Loan Repayment</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? (
                                  format(field.value, "PPP")
                                ) : (
                                  <span>Pick loan repayment date</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              // disabled={(date) =>
                              //   date > new Date() ||
                              //   date < new Date("1900-01-01")
                              // }
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator className="my-4 mt-8" />

                  <FormField
                    control={form.control}
                    name="attachment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Upload Attachment</FormLabel>
                        <FormControl>
                          <UploadAttachment
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <AlertDialogFooter className="mt-5">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <Button type="submit" disabled={isPending}>
                    Create
                  </Button>
                </AlertDialogFooter>
              </form>
            </Form>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
